import { useMemo } from 'react';
import { useUserDirectory } from '../users/directory';

/**
 * Users the owner and assignee pickers can offer: the organization's active users from the directory
 * (`GET /users`, M8) plus the users the caller has already seen on the record (e.g. a deactivated owner, so the
 * current value stays visible). The server validates every choice (span rules, invariant 6), so an option outside
 * the caller's reach answers with its German field error.
 */
export interface KnownUser {
  id: string;
  name: string;
  shortName: string;
}

export function useKnownUsers(seen: readonly KnownUser[] = []): KnownUser[] {
  const directory = useUserDirectory();
  return useMemo(() => {
    const byId = new Map<string, KnownUser>();
    for (const user of directory.data ?? []) {
      byId.set(user.id, { id: user.id, name: user.name, shortName: user.shortName });
    }
    for (const user of seen) if (!byId.has(user.id)) byId.set(user.id, user);
    return [...byId.values()].sort((a, b) => a.shortName.localeCompare(b.shortName, 'de'));
  }, [directory.data, seen]);
}

/** A `UserRef` of the API ("J. Weiler") as a known user (short name only). */
export function knownFromRef(ref: { id: string; name: string } | null | undefined): KnownUser[] {
  return ref ? [{ id: ref.id, name: ref.name, shortName: ref.name }] : [];
}
